import { faFaceScream } from "@fortawesome/pro-regular-svg-icons"
import { faClone } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, CircularProgress, Typography } from "@mui/material"
import { translate } from "app/language/service"
import { IRootState } from "app/model"
import { deepEquals } from "app/tools/common"
import { FieldDate } from "form/components/fields/FieldDate"
import { FieldSelect } from "form/components/fields/FieldSelect"
import { Form, Formik } from "formik"
import React from "react"
import { connect } from "react-redux"
import { FlexBox } from "ui/components/FlexBox"
import { LinearLoader } from "ui/components/LinearLoader"
import { addImportant } from "ui/tools/common"
import { isTabletScreen } from "ui/tools/responsive"
import { IUser, IUserImpersonator, IUserImpersonatorHydrated, IUserInfo, IUserNewImpersonatorParams } from "user/model"
import { getMyAgencyMembers } from "user/tools/agency"
import { getUserId, getUserName } from "user/tools/common"
import {
  addImpersonator,
  hydrateImpersonator,
  updateImpersonator,
  validateImpersonatorFormData
} from "user/tools/impersonate"

interface IImpersonatorFormConnectProps {
  isTablet: boolean
  selfUserInfo: IUserInfo
}

interface IImpersonatorFormProps {
  existingImpersonators: IUserImpersonator[]
  impersonator?: IUserImpersonatorHydrated // undefined = creation
  onSave: (impersonator: IUserImpersonatorHydrated) => any
  onCancel: () => any
}

const ImpersonatorFormFn = React.memo(
  ({
    impersonator,
    existingImpersonators,
    isTablet,
    selfUserInfo,
    onSave,
    onCancel
  }: IImpersonatorFormProps & IImpersonatorFormConnectProps) => {
    const [initialImpersonator, setInitialImpersonator] = React.useState<IUserNewImpersonatorParams>(
      impersonator
        ? { ...impersonator }
        : {
            impersonator_id: -1,
            user_id: getUserId()
          }
    )
    const [impersonatorData, setImpersonatorData] = React.useState<IUserNewImpersonatorParams>(
      impersonator
        ? { ...impersonator }
        : {
            impersonator_id: -1,
            user_id: getUserId()
          }
    )
    const [agencyUsers, setAgencyUsers] = React.useState<IUser[] | null>(null)
    const [hasLoadingUsersError, setHasLoadingUsersError] = React.useState(false)
    const [isSaving, setIsSaving] = React.useState(false)
    const existingInpersonatorIds = React.useMemo(
      () => existingImpersonators.map(i => i.impersonator_id),
      [existingImpersonators]
    )
    const isCreation = React.useMemo(() => impersonator === undefined, [impersonator])
    const hasChanged = React.useMemo(
      () => !deepEquals(impersonatorData, initialImpersonator),
      [impersonatorData, initialImpersonator]
    )
    React.useEffect(() => {
      getMyAgencyMembers()
        .then(users =>
          setAgencyUsers(
            users.filter(
              u =>
                u.id !== selfUserInfo.user.id &&
                (!existingInpersonatorIds.includes(u.id) || (impersonator && impersonator.impersonator_id === u.id))
            )
          )
        )
        .catch(error => {
          console.error(error)
          setHasLoadingUsersError(true)
        })
    }, [])
    if (hasLoadingUsersError) {
      return (
        <Typography color="error" fontSize=".9rem">
          {translate("label.list.error")}
        </Typography>
      )
    } else if (agencyUsers === null) {
      return <LinearLoader />
    } else if (agencyUsers.length === 0) {
      return (
        <FlexBox vertical gap fullWidth>
          <FlexBox fullWidth gap sx={{ "& .icon": { color: theme => theme.palette.primary.main } }}>
            <FontAwesomeIcon icon={faFaceScream} size="3x" className="icon" />
            <Typography color="textPrimary" fontSize=".9rem">
              {translate("user.agency.alone")}
            </Typography>
          </FlexBox>
          <FlexBox fullWidth jc="end">
            <Button variant="contained" onClick={onCancel}>
              {translate("button.close")}
            </Button>
          </FlexBox>
        </FlexBox>
      )
    }
    return (
      <FlexBox fullWidth vertical gap sx={{ maxWidth: "100%" }}>
        <Formik
          initialValues={impersonatorData}
          validate={(values: IUserNewImpersonatorParams) => {
            setImpersonatorData(values)
            return validateImpersonatorFormData(values)
          }}
          onSubmit={async (_values, actions) => {
            if (!hasChanged) {
              actions.setSubmitting(false)
              return
            }
            if (isSaving) {
              return
            }
            setIsSaving(true)
            let userImpersonator: IUserImpersonatorHydrated | null | undefined | "error"
            if (isCreation) {
              userImpersonator = await hydrateImpersonator(await addImpersonator(impersonatorData))
            } else {
              userImpersonator = await hydrateImpersonator(
                await updateImpersonator(impersonatorData as IUserImpersonator)
              )
            }
            setIsSaving(false)
            actions.setSubmitting(false)
            if (userImpersonator !== null && typeof userImpersonator === "object") {
              onSave(userImpersonator)
            }
          }}>
          {({ errors, isSubmitting, setFieldValue, setValues, values }) => {
            const canSubmit = !isSubmitting && Object.keys(errors).length === 0
            return (
              <Form style={{ width: "100%", padding: ".5rem", maxWidth: "100%" }}>
                <FlexBox
                  wrap
                  gap
                  vertical
                  fullWidth
                  ai="start"
                  sx={{
                    "maxWidth": "100%",
                    "height": "100%",
                    "minHeight": "100%",
                    "maxHeight": "100%",
                    "& > *": {
                      width: addImportant(!isTablet ? "26rem" : "100%")
                    }
                  }}>
                  <FieldSelect
                    fieldName="impersonator_id"
                    label={translate("label.select.user")}
                    value={impersonatorData.impersonator_id}
                    options={agencyUsers.map(u => u.id)}
                    optionLabelFn={userId => {
                      const user = agencyUsers.find(u => u.id === userId)
                      return getUserName(user)
                    }}
                    required
                    disabled={!isCreation}
                  />
                  <FieldDate
                    fieldName="start_date"
                    label={translate("label.startDate")}
                    value={impersonatorData.start_date ? new Date(impersonatorData.start_date) : null}
                    error={errors.start_date}
                    minTime
                    toTouchFieldName={"end_date"}
                  />
                  <FieldDate
                    fieldName="end_date"
                    label={translate("label.endDate")}
                    value={impersonatorData.end_date ? new Date(impersonatorData.end_date) : null}
                    error={errors.end_date}
                    maxTime
                  />
                </FlexBox>
                <FlexBox gap fullWidth ai="end" jc="end" sx={{ mt: "2rem", width: !isTablet ? "26rem" : "100%" }}>
                  <Button
                    id="impersonator-form-button-cancel"
                    variant="outlined"
                    onClick={() => onCancel()}
                    disabled={isSaving || (!onCancel && !hasChanged)}>
                    {translate("button.cancel")}
                  </Button>
                  <Button
                    id="impersonator-form-button-ok"
                    variant="contained"
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress sx={{ color: theme => theme.palette.primary.main }} size="1.5rem" />
                      ) : (
                        <FontAwesomeIcon icon={faClone} />
                      )
                    }
                    type="submit"
                    disabled={!hasChanged || !canSubmit || isSaving}>
                    {translate(isCreation ? "button.add" : "button.update")}
                  </Button>
                </FlexBox>
              </Form>
            )
          }}
        </Formik>
      </FlexBox>
    )
  }
)

export const ImpersonatorForm = connect(
  (state: IRootState): IImpersonatorFormConnectProps => ({
    isTablet: isTabletScreen(state),
    selfUserInfo: state.user
  })
)(ImpersonatorFormFn)
